import React, { useState } from 'react';
import { unifiedTasksApi } from '../../api/unifiedTasks';
import { Claim } from '../../api/claims';
import { Button } from '../../components/ui';
import { ClaimPicker } from './ClaimPicker';
import './lanes.css';

/**
 * Standalone "Create Task" dialog, extracted from the legacy Tasks.tsx
 * (Tasks Rebuild Task 8) so the lane shell doesn't depend on the 4,900-line
 * page that's being decommissioned. State/JSX/behaviour is a direct port of
 * Tasks.tsx's createTaskParams/handleCreateTask/resetCreateDialog +
 * "Create Task Dialog" JSX block, just made self-contained.
 *
 * Task 12: the inline debounced claim-search block (its own copy of the
 * search-input/results-list idiom) is replaced with the shared `ClaimPicker`
 * component -- see task-12-report.md's dedupe note.
 */

interface CreateTaskParams {
  type: string;
  subject: string;
  description: string;
  claimId: string;
  dueDate: string;
  priority: 'high' | 'medium' | 'low';
}

const INITIAL_PARAMS: CreateTaskParams = {
  type: 'manual',
  subject: '',
  description: '',
  claimId: '',
  dueDate: '',
  priority: 'medium',
};

export interface CreateTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful create so the caller can refresh lists/counts. */
  onCreated?: () => void;
}

export const CreateTaskModal: React.FC<CreateTaskModalProps> = ({ isOpen, onClose, onCreated }) => {
  const [params, setParams] = useState<CreateTaskParams>(INITIAL_PARAMS);
  const [selectedClaim, setSelectedClaim] = useState<Claim | null>(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setParams(INITIAL_PARAMS);
    setSelectedClaim(null);
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleCreate = async () => {
    if (!params.subject.trim()) {
      setError('Subject is required');
      return;
    }
    try {
      setProcessing(true);
      setError(null);
      const payload: Parameters<typeof unifiedTasksApi.createTask>[0] = {
        type: params.type,
        subject: params.subject,
        description: params.description,
      };
      if (params.claimId) payload.claimId = params.claimId;
      if (params.dueDate) payload.dueDate = params.dueDate;
      if (params.priority) payload.priority = params.priority;

      await unifiedTasksApi.createTask(payload);
      reset();
      onClose();
      onCreated?.();
    } catch (e) {
      console.error('Error creating task:', e);
      setError('Failed to create task');
    } finally {
      setProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="lane-modal-backdrop" onClick={handleClose}>
      <div className="lane-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="lane-modal__title">Create New Task</h2>

        {error && <div className="lane-modal__error">{error}</div>}

        <div className="lane-modal__field">
          <label>Task Type:</label>
          <select
            value={params.type}
            onChange={(e) => setParams({ ...params, type: e.target.value })}
            className="lane-modal__input"
          >
            <option value="manual">Manual Task</option>
            <option value="email_lender_response">Lender Response</option>
            <option value="email_lender_query">Lender Query</option>
            <option value="email_client">Client Email</option>
            <option value="email_other">Other Email</option>
          </select>
        </div>

        <div className="lane-modal__field">
          <label>Subject: *</label>
          <input
            type="text"
            value={params.subject}
            onChange={(e) => setParams({ ...params, subject: e.target.value })}
            className="lane-modal__input"
            placeholder="Enter task subject..."
            required
          />
        </div>

        <div className="lane-modal__field">
          <label>Description:</label>
          <textarea
            value={params.description}
            onChange={(e) => setParams({ ...params, description: e.target.value })}
            className="lane-modal__input"
            rows={4}
            placeholder="Enter task description..."
          />
        </div>

        <ClaimPicker
          label="Assign to Claim (optional):"
          selectedClaim={selectedClaim}
          onSelect={(claim) => { setSelectedClaim(claim); setParams((p) => ({ ...p, claimId: claim.id })); }}
          formatMeta={(claim) => (
            <>{claim.email} &bull; {claim.craFinanceLender || (claim as any).lender || 'No lender'} &bull; ID: {claim.id}</>
          )}
        />

        <div className="lane-modal__field">
          <label>Due Date:</label>
          <input
            type="date"
            value={params.dueDate}
            onChange={(e) => setParams({ ...params, dueDate: e.target.value })}
            className="lane-modal__input"
            min={new Date().toISOString().split('T')[0]}
          />
        </div>

        <div className="lane-modal__field">
          <label>Priority:</label>
          <select
            value={params.priority}
            onChange={(e) => setParams({ ...params, priority: e.target.value as 'high' | 'medium' | 'low' })}
            className="lane-modal__input"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>

        <div className="lane-modal__actions">
          <Button variant="secondary" onClick={handleClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleCreate}
            disabled={processing || !params.subject.trim()}
          >
            Create Task
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateTaskModal;
